import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { cn } from "../../lib/utils";
import { stageMmOpen } from "../../lib/mmHandoff";
import { mmTypeStyle } from "../../lib/mmTypeStyle";
import type { UnderneathMap } from "../../hooks/useUnderneath";

const MAX_CHIPS = 2;

const clip = (s: string, n = 40) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/**
 * "Underneath: <model>" chips (spec 2026-11-03 §D). Reads one entity's entry
 * from a list's batched useUnderneath map; renders nothing when there is none.
 */
const UnderneathChips: React.FC<{
  entityId: string;
  byId?: UnderneathMap;
  className?: string;
}> = ({ entityId, byId, className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items = byId?.[entityId] ?? [];
  if (items.length === 0) return null;

  const open = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    stageMmOpen(id);
    window.dispatchEvent(new CustomEvent("portal-tab-change", { detail: "my_workspace" }));
    navigate("/portal");
  };
  const rest = items.length - MAX_CHIPS;

  return (
    <span className={cn("inline-flex flex-wrap items-center gap-1", className)}>
      <span className="text-[11px] text-muted-foreground/70">{t("mm_underneath", "Underneath:")}</span>
      {items.slice(0, MAX_CHIPS).map((m) => {
        const style = mmTypeStyle(m.objectType);
        const Icon = style.icon;
        return (
          <button
            key={m.id}
            type="button"
            dir="auto"
            onClick={(e) => open(e, m.id)}
            title={m.label}
            className={cn(
              "inline-flex items-center gap-1 max-w-[220px] rounded-full border px-2 py-px text-[11px] transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
              style.chip
            )}
          >
            <Icon className="w-3 h-3 flex-none" />
            <span className="truncate">{clip(m.label)}</span>
          </button>
        );
      })}
      {rest > 0 && (
        <span
          className="text-[11px] text-muted-foreground"
          title={items
            .slice(MAX_CHIPS)
            .map((m) => m.label)
            .join("\n")}
        >
          {t("mm_underneath_more", "+{{count}}", { count: rest })}
        </span>
      )}
    </span>
  );
};

export default UnderneathChips;
